import dayjs from 'dayjs'
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'

import { DatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'

import CreateIcon from '@mui/icons-material/Palette'
import ManagerIcon from '@mui/icons-material/AdminPanelSettings'
import SchoolIcon from '@mui/icons-material/School'
import ApprovalIcon from '@mui/icons-material/Approval'
import BarChartIcon from '@mui/icons-material/BarChart'
import PolicyIcon from '@mui/icons-material/Policy'
import PeopleAltIcon from '@mui/icons-material/PeopleAlt'
import MenuBookIcon from '@mui/icons-material/MenuBook'

import { ReactElement, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useFormik } from 'formik'
import * as yup from 'yup'
import axios from 'src/utilities/http-client'
import withConfirm from 'src/components/common/with-confirm'
import { always, andThen, compose, pipe } from 'ramda'
import StateDropdown from 'src/v3/components/form-fields/state-dropdown'
import Subheader from '../../components/subheader'
import Interventions from './interventions'
import {
  fetchClasses,
  fetchCourses,
  fetchLinkedServiceUsers,
  fetchLinkedServices,
  fetchUsers,
  linkedServiceUserRemoved,
  selectClassesStatus,
  selectCoursesStatus,
  selectLinkedServiceByName,
  selectLinkedServices,
  selectLinkedServicesByUserId,
  selectLinkedServicesStatus,
  selectProfileImageByUserID,
  selectUserByEmail,
  selectUserRolesByUserId,
  selectUsers,
  selectUsersStatus,
  resetUsers,
  selectRoles,
  selectRolesStatus,
  fetchRoles,
  fetchUserRoles,
  selectSubscriptionByUserID,
  selectUserAccessLevelByUserID,
  fetchUserSubscriptions,
  selectBoundTreeLimitedAccessLevel,
  selectBoundTreeFullAccessLevel,
  selectAccessLevelsStatus,
  fetchAccessLevels,
  fetchAllVideos,
  selectAllVideosStatus,
} from '../admin.slice'
import { CancelIcon, EditIcon, ScribeIcon, SubmitIcon } from '../../core/icons'

export default (): ReactElement => {
  let dispatch = useAppDispatch()

  let [open, setOpen] = useState(false)
  let [formOpen, setFormOpen] = useState(false)
  let [formSaving, setFormSaving] = useState(false)
  let [subscriptionID, setSubscriptionID] = useState<string>('')
  let [subscriptionStatus, setSubscriptionStatus] = useState<string>('')
  let [searchValue, setSearchValue] = useState<string>('')
  let [selectedUser, setSelectedUser] = useState<string>('')
  let [selectedLinkedService, setSelectedLinkedService] = useState<string>('')

  let accessLevelsStatus = useAppSelector(selectAccessLevelsStatus)
  let boundtreeLimited = useAppSelector(selectBoundTreeLimitedAccessLevel)
  let boundtreeFull = useAppSelector(selectBoundTreeFullAccessLevel)
  let [modalStatus, setModalStatus] = useState<
    | 'None'
    | 'AddLinkedService'
    | 'DeleteLinkedService'
    | 'EditUserRole'
    | 'DeleteUser'
    | 'ReallyDeleteUser'
    | 'GrantBoundTreeFullAccess'
    | 'GrantBoundTreeLimitedAccess'
    | 'RemoveBoundTreeAccess'
  >('None')
  let [linkedServiceToRemove, setLinkedServiceToRemove] = useState<string>('')
  let [roleToChange, setRoleToChange] = useState<string>('')
  let [errorMessage, setErrorMessage] = useState<string>('')
  let [successMessage, setSuccessMessage] = useState<string>('')

  let copyToClipboard = (text: string, label: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setSuccessMessage(`${label} copied to clipboard!`)
      })
      .catch(() => {
        setErrorMessage('Failed to copy to clipboard')
      })
  }
  let [userFormStatus, setUserFormStatus] = useState<string>('')
  let [saving, setSaving] = useState<boolean>(false)

  let usersStatus = useAppSelector(selectUsersStatus)
  let users = useAppSelector(selectUsers)
  let user = useAppSelector(selectUserByEmail(selectedUser))
  let profileImage = useAppSelector(selectProfileImageByUserID(user?.id ?? ''))
  let linkedServices = useAppSelector(
    selectLinkedServicesByUserId(user?.id ?? ''),
  )
  let userRoles = useAppSelector(selectUserRolesByUserId(user?.id ?? ''))
  let roles = useAppSelector(selectRoles)
  let rolesStatus = useAppSelector(selectRolesStatus)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let allLinkedServices = useAppSelector(selectLinkedServices)
  let classesStatus = useAppSelector(selectClassesStatus)
  let coursesStatus = useAppSelector(selectCoursesStatus)
  let linkedServiceToAdd = useAppSelector(
    selectLinkedServiceByName(selectedLinkedService),
  )
  let userSubscription = useAppSelector(
    selectSubscriptionByUserID(user?.id ?? ''),
  )
  let userAccessLevel = useAppSelector(
    selectUserAccessLevelByUserID(user?.id ?? ''),
  )
  let userRoleAction = userRoles.some((ur) => ur.roleName === roleToChange)
    ? 'remove'
    : 'add'
  let videosStatus = useAppSelector(selectAllVideosStatus)

  useEffect(() => {
    if (linkedServicesStatus === 'uninitialized') {
      dispatch(fetchLinkedServices())
    }
  }, [linkedServicesStatus])

  useEffect(() => {
    if (classesStatus === 'uninitialized') {
      dispatch(fetchClasses())
    }
  }, [classesStatus])

  useEffect(() => {
    if (rolesStatus === 'uninitialized') {
      dispatch(fetchRoles())
    }
  }, [rolesStatus])

  useEffect(() => {
    if (accessLevelsStatus === 'uninitialized') {
      dispatch(fetchAccessLevels())
    }
  }, [accessLevelsStatus])

  useEffect(() => {
    if (videosStatus === 'uninitialized') {
      dispatch(fetchAllVideos())
    }
  }, [videosStatus])

  useEffect(() => {
    if (coursesStatus === 'uninitialized') {
      dispatch(fetchCourses())
    }
  }, [coursesStatus])

  useEffect(() => {
    if (usersStatus === 'uninitialized') {
      dispatch(fetchUsers(''))
    }
  }, [usersStatus])

  let closeSubscriptionForm = () => {
    setFormOpen(false)
    setSubscriptionID('')
    setSubscriptionStatus('')
  }

  let userForm = useFormik({
    validationSchema: yup.object({
      firstName: yup.string().required(),
      lastName: yup.string().required(),
      phone: yup.string().required(),
      username: yup.string().required(),
      email: yup.string().required(),
      address: yup.string().required(),
      city: yup.string().required(),
      state: yup.string().not(['unselected']).required(),
      zip: yup.string().required(),
    }),
    enableReinitialize: true,
    initialValues: {
      firstName: '',
      lastName: '',
      phone: '',
      username: '',
      email: '',
      address: '',
      city: '',
      state: '',
      zip: '',
    },
    onSubmit: (values) => {
      setSaving(true)
      axios
        .put('/api/v2/admin/user', {
          firstName: values.firstName,
          lastName: values.lastName,
          id: user?.id,
          phone: values.phone,
          email: values.username,
          primaryEmail: values.email,
          address: values.address,
          city: values.city,
          state: values.state,
          zip: values.zip,
        })
        .then(() => dispatch(fetchUsers(searchValue)))
        .then(() => setUserFormStatus(''))
        .then(() => setSaving(false))
        .then(() => userForm.resetForm())
        .catch((err) => {
          setErrorMessage(err?.message || 'Error Updating User Information')
          dispatch(fetchUsers(searchValue))
          setUserFormStatus('')
          setSaving(false)
        })
    },
  })

  let licenseForm = useFormik({
    validationSchema: yup.object({
      licenseNumber: yup.string(),
      licenseType: yup.string().required(),
      licenseState: yup.string().not(['unselected']).required(),
      licenseExpiration: yup.date(),
    }),
    enableReinitialize: true,
    initialValues: {
      licenseNumber: '',
      licenseType: '',
      licenseState: '',
      licenseExpiration: '',
    },
    onSubmit: (values) => {
      setSaving(true)
      axios
        .put('/api/admin/user-primary-license', {
          userID: user?.id,
          number: values.licenseNumber,
          type: values.licenseType,
          state: values.licenseState,
          expiration: values.licenseExpiration,
        })
        .then(() => dispatch(fetchUsers(searchValue)))
        .then(() => setUserFormStatus(''))
        .then(() => setSaving(false))
        .then(() => licenseForm.resetForm())
        .catch((err) => {
          setErrorMessage(err?.message || 'Error Updating User Information')
          dispatch(fetchUsers(searchValue))
          setUserFormStatus('')
          setSaving(false)
        })
    },
  })

  let nremtForm = useFormik({
    validationSchema: yup.object({
      nremtCertificationNumber: yup.string().matches(/[REAM]\d+/),
      nremtCertificationDate: yup.date(),
    }),
    enableReinitialize: true,
    initialValues: {
      nremtCertificationNumber: '',
      nremtCertificationDate: '',
      issueDate: '',
    },
    onSubmit: (values) => {
      setSaving(true)
      axios
        .put('/api/admin/user-nremt-license', {
          userID: user?.id,
          nremtIdNumber: values.nremtCertificationNumber,
          expiration: values.nremtCertificationDate,
          issueDate: values.issueDate || undefined,
        })
        .then(() => dispatch(fetchUsers(searchValue)))
        .then(() => setUserFormStatus(''))
        .then(() => setSaving(false))
        .then(() => nremtForm.resetForm())
        .catch((err) => {
          setErrorMessage(err?.message || 'Error Updating User Information')
          dispatch(fetchUsers(searchValue))
          setUserFormStatus('')
          setSaving(false)
        })
    },
  })

  let onUserEdit = () => {
    userForm.setFieldValue('firstName', user?.firstName)
    userForm.setFieldValue('lastName', user?.lastName)
    userForm.setFieldValue('phone', user?.phone)
    userForm.setFieldValue('username', user?.email)
    userForm.setFieldValue('email', user?.primaryEmail)
    userForm.setFieldValue('address', user?.address)
    userForm.setFieldValue('city', user?.city)
    userForm.setFieldValue('state', user?.state)
    userForm.setFieldValue('zip', user?.zip)
    setUserFormStatus('user')
  }

  let onLicenseEdit = () => {
    licenseForm.setFieldValue('licenseNumber', user?.licenseNumber)
    licenseForm.setFieldValue('licenseType', user?.licenseType)
    licenseForm.setFieldValue('licenseState', user?.licenseState)
    licenseForm.setFieldValue('licenseExpiration', user?.licenseExpiration)
    setUserFormStatus('license')
  }

  let onNremtEdit = () => {
    nremtForm.setFieldValue(
      'nremtCertificationNumber',
      user?.nremtCertificationNumber,
    )
    nremtForm.setFieldValue(
      'nremtCertificationDate',
      user?.nremtCertificationDate,
    )
    nremtForm.setFieldValue('issueDate', user?.nremtIssueDate)
    setUserFormStatus('nremt')
  }

  let addUserToLinkedService = () =>
    axios
      .post('/api/v2/admin/linked-service/user', {
        userID: user?.id,
        linkedServiceID: linkedServiceToAdd?.id,
      })
      .then(() => {
        dispatch(fetchLinkedServiceUsers([user?.id ?? '']))
        setSelectedLinkedService('')
      })
      .catch((err) => {
        setErrorMessage(err?.response?.data)
      })

  let removeUserFromLinkedService = () =>
    axios
      .delete(
        `/api/v2/admin/linked-service/user?userID=${user?.id}&linkedServiceID=${linkedServiceToRemove}`,
      )
      .then(() => {
        dispatch(
          linkedServiceUserRemoved([user?.id ?? '', linkedServiceToRemove]),
        )
        setLinkedServiceToRemove('')
      })
      .catch((err) => {
        setErrorMessage(err?.response?.data)
      })

  let changeUserRole = () =>
    axios
      .put('/api/v2/admin/user-roles', {
        userID: user?.id,
        roleID: roles.find((r) => r.name === roleToChange)?.id,
        action: userRoleAction,
      })
      .then(() => setModalStatus('None'))
      .then(() => setRoleToChange(''))
      .then(() => dispatch(fetchUserRoles([user?.id || ''])))
      .catch((err) => {
        setErrorMessage(err?.response?.data)
      })

  let deleteUser = () =>
    axios
      .delete(`/api/v2/admin/user?userID=${user?.id}`)
      .then(() => {
        setSelectedUser('')
        dispatch(resetUsers())
      })
      .catch((err) => {
        setErrorMessage(err?.response?.data)
      })

  let makeBoundTreeLimited = () =>
    axios
      .put('/api/v2/admin/access-level/user', {
        userID: user?.id,
        accessLevelID: boundtreeLimited?.id,
      })
      .then(() => dispatch(fetchUsers(user?.email ?? '')))
      .catch((err) => {
        setErrorMessage(err?.response?.data)
      })

  let makeBoundTreeFull = () =>
    axios
      .put('/api/v2/admin/access-level/user', {
        userID: user?.id,
        accessLevelID: boundtreeFull?.id,
      })
      .then(() => dispatch(fetchUsers(user?.email ?? '')))
      .catch((err) => {
        setErrorMessage(err?.response?.data)
      })

  let deleteBoundTreeAccess = () =>
    axios
      .delete(`/api/v2/admin/access-level/user?userID=${user?.id}`)
      .then(() => dispatch(fetchUsers(user?.email ?? '')))
      .catch((err) => {
        setErrorMessage(err?.response?.data)
      })

  let onUserSubscriptionUpdated = () => {
    if (user) {
      setFormSaving(true)
      axios
        .put('/api/v2/admin/subscription', {
          primaryEmail: user?.primaryEmail,
          subscriptionID,
          subscriptionStatus,
        })
        .catch((err) => {
          setFormSaving(false)
          setErrorMessage(
            err?.response?.data ||
              err?.message ||
              'Error Updating Subscription Info',
          )
        })
        .then(() => dispatch(fetchUserSubscriptions([user!.id])))
        .then(() => setSubscriptionID(''))
        .then(() => setSubscriptionStatus(''))
        .then(() => setFormSaving(false))
        .then(() => closeSubscriptionForm())
    }
  }

  let ConfirmAddToLinkedService = withConfirm(
    'Add User to Linked Service',
    `Are you sure you want to add ${user?.firstName} ${user?.lastName} to ${linkedServiceToAdd?.name}?`,
    modalStatus === 'AddLinkedService',
    compose(setModalStatus, always('None') as any),
    pipe(
      addUserToLinkedService,
      andThen(compose(setModalStatus, always('None') as any)),
    ),
  )

  let ConfirmRemoveFromLinkedService = withConfirm(
    'Remove User From Linked Service',
    `Are you sure you want to remove ${user?.firstName} ${user?.lastName} from this service?`,
    modalStatus === 'DeleteLinkedService',
    compose(setModalStatus, always('None') as any),
    pipe(
      removeUserFromLinkedService,
      andThen(compose(setModalStatus, always('None') as any)),
    ),
  )

  let getRoleWarningInfo = () => {
    if (userRoleAction === 'add') {
      if (roleToChange === 'scribe') {
        return 'The Scribe role grants access to confidential company business intelligence and sensitive data. Only grant this role to trusted FOAMfrat employees or authorized personnel.'
      }
      if (roleToChange === 'content-analyzer') {
        return 'The Content Analyzer role grants access to confidential company business intelligence, performance metrics, and sensitive accreditation data. Only grant this role to trusted FOAMfrat employees or authorized personnel.'
      }
      if (roleToChange === 'content-planner') {
        return "The Content Planner role grants access to internal company intellectual property and confidential information about FOAMfrat's content development processes. Only grant this role to trusted FOAMfrat employees or authorized personnel."
      }
      if (roleToChange === 'content-instructor') {
        return 'The Content Instructor role grants access to internal live class schedules and instructor sign-up features in the Content Pipeline. Only grant this role to trusted FOAMfrat employees or authorized instructors.'
      }
      if (roleToChange === 'codex-editor') {
        return 'The Codex Editor role grants permissions to create, edit, and manage FOAMfrat intellectual property including educational books, chapters, and proprietary content in the Codex system. This role provides direct access to create and modify company-owned educational materials. Only grant this role to trusted FOAMfrat employees, contractors, or authorized content creators.'
      }
    }
    return null
  }

  let ConfirmChangeUserRole = () => {
    let warning = getRoleWarningInfo()

    return (
      <Dialog
        open={modalStatus === 'EditUserRole'}
        onClose={compose(setModalStatus, always('None') as any)}
      >
        <DialogTitle>Edit User Role</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to {userRoleAction} the {roleToChange} role
            for {user?.firstName} {user?.lastName}?
          </Typography>
          {warning && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {warning}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary">
            This action will be logged to the audit log.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={compose(setModalStatus, always('None') as any) as any}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={
              pipe(
                changeUserRole,
                andThen(compose(setModalStatus, always('None') as any)),
              ) as any
            }
            color="primary"
            variant="contained"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  let ConfirmDeleteUser = withConfirm(
    'Delete User?',
    `WARNING! Deleting a user is an irreversible action. It is NOT our practice to delete innactive or cancelled users. It is important that innactive accounts are not deleted so that historical data can still be accessed for our own purposes, as well as for auditing purposes such as for CAPCE and other accreditation agencies.
    
    The only acceptable case for deleting a user's account is if they insist that their personal information be removed as per our privacy policy.
    
    Are you sure you want to delete ${user?.firstName} ${user?.lastName}?`,
    modalStatus === 'DeleteUser',
    compose(setModalStatus, always('None') as any),
    compose(setModalStatus, always('ReallyDeleteUser') as any),
  )

  let ReallyConfirmDeleteUser = withConfirm(
    'Are you absolutely sure?',
    'The devs cannot save you.',
    modalStatus === 'ReallyDeleteUser',
    compose(setModalStatus, always('None') as any),
    pipe(deleteUser, compose(setModalStatus, always('None') as any)),
  )

  let ConfirmBoundTreeLimited = withConfirm(
    'Bound Tree Access',
    'Are you sure you want to grant this user Bound Tree limited access?',
    modalStatus === 'GrantBoundTreeLimitedAccess',
    compose(setModalStatus, always('None') as any),
    pipe(makeBoundTreeLimited, compose(setModalStatus, always('None') as any)),
  )

  let ConfirmBoundTreeFull = withConfirm(
    'Bound Tree Access',
    'Are you sure you want to grant this user Bound Tree full access?',
    modalStatus === 'GrantBoundTreeFullAccess',
    compose(setModalStatus, always('None') as any),
    pipe(makeBoundTreeFull, compose(setModalStatus, always('None') as any)),
  )

  let ConfirmRemoveBoundtree = withConfirm(
    'Bound Tree Access',
    'Are you sure you want to remove this user from Bound Tree?',
    modalStatus === 'RemoveBoundTreeAccess',
    compose(setModalStatus, always('None') as any),
    pipe(deleteBoundTreeAccess, compose(setModalStatus, always('None') as any)),
  )

  return (
    <>
      <Container maxWidth="xl">
        <Typography variant="h1">User Administration</Typography>
        <Autocomplete
          open={open}
          onOpen={() => {
            setOpen(true)
          }}
          onClose={() => {
            setOpen(false)
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) =>
            `${option.firstName} ${option.lastName} | ${option.primaryEmail} | ${option.email}`
          }
          onInputChange={(_, value) => setSelectedUser(value.split(' | ')[1])}
          options={users}
          loading={usersStatus === 'pending'}
          renderInput={(params) => (
            <TextField
              {...params}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  dispatch(fetchUsers(searchValue))
                }
              }}
              onChange={(e) => setSearchValue(e.target.value)}
              label="Username or Email"
              sx={{
                mb: 4,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.paperDark',
                },
              }}
              slotProps={{
                ...params.slotProps,

                input: {
                  ...params.slotProps.input,
                  endAdornment: (
                    <>
                      {
                        <SearchIcon
                          sx={{ cursor: 'pointer' }}
                          onClick={() => dispatch(fetchUsers(searchValue))}
                        />
                      }
                      {params.slotProps.input.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />
        {user && (
          <>
            <Subheader title="User Profile" />
            <Paper
              variant="outlined"
              className="w-full"
              sx={{
                p: 3,
                bgcolor: 'background.paper',
                borderRadius: 2,
              }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-3">
                {/* Avatar Section */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  {profileImage ? (
                    <Avatar
                      sx={{ width: 120, height: 120 }}
                      src={profileImage.imageName}
                    />
                  ) : (
                    <Avatar
                      sx={{
                        width: 120,
                        height: 120,
                        fontSize: 48,
                      }}
                    >
                      {`${user?.firstName[0]}${user?.lastName[0]}`.toUpperCase()}
                    </Avatar>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mt: 2,
                    }}
                  >
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      {`${user?.firstName} ${user?.lastName}`}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={onUserEdit}
                      sx={{ '&:hover': { bgcolor: 'background.paperDark' } }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Box>
                  {userAccessLevel ? (
                    <Chip
                      label={`BT ${userAccessLevel.accessLevel}`}
                      onClick={() => {
                        setModalStatus(
                          userAccessLevel?.accessLevel.toLowerCase() ===
                            'limited'
                            ? 'GrantBoundTreeFullAccess'
                            : 'GrantBoundTreeLimitedAccess',
                        )
                      }}
                      variant="filled"
                      onDelete={() => {
                        setModalStatus('RemoveBoundTreeAccess')
                      }}
                      sx={{
                        mt: 2,
                        color: 'black',
                        backgroundColor: '#ffd650',
                      }}
                    />
                  ) : (
                    <Chip
                      label={`No Access Level`}
                      variant="outlined"
                      onClick={() => {
                        setModalStatus('GrantBoundTreeLimitedAccess')
                      }}
                      sx={{
                        mt: 2,
                        color: '#ffd650',
                      }}
                    />
                  )}
                </Box>

                {/* Contact Info Section */}
                <Box className="col-span-2">
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Contact Information
                  </Typography>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
                  >
                    {/* User ID */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ minWidth: '80px', fontWeight: 500 }}
                      >
                        User ID:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user?.id}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() =>
                          copyToClipboard(user?.id || '', 'User ID')
                        }
                        sx={{ '&:hover': { bgcolor: 'background.paperDark' } }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* Primary Email */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ minWidth: '80px', fontWeight: 500 }}
                      >
                        Email:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user?.primaryEmail}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() =>
                          copyToClipboard(user?.primaryEmail || '', 'Email')
                        }
                        sx={{ '&:hover': { bgcolor: 'background.paperDark' } }}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* Phone */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ minWidth: '80px', fontWeight: 500 }}
                      >
                        Phone:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user?.phone || 'Not provided'}
                      </Typography>
                    </Box>

                    {/* Address */}
                    <Box
                      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ minWidth: '80px', fontWeight: 500 }}
                      >
                        Address:
                      </Typography>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {user?.address || 'Not provided'}
                        </Typography>
                        {user && user.city && user.state && user.zip && (
                          <Typography variant="body2" color="text.secondary">
                            {user?.city}, {user?.state} {user?.zip}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Spacer for 3-column layout */}
                <Box />
              </div>
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Roles
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    mb: 3,
                  }}
                >
                  {[
                    {
                      name: 'Manager',
                      icon: <ManagerIcon />,
                      tooltip:
                        'Manage users and assignments at a linked service',
                    },
                    {
                      name: 'Creator',
                      icon: <CreateIcon />,
                      tooltip: 'Create content in the LMS',
                    },
                    {
                      name: 'Student',
                      icon: <SchoolIcon />,
                      tooltip: 'Access basic learning features',
                    },
                    {
                      name: 'Scribe',
                      icon: <ScribeIcon />,
                      tooltip: 'Access FOAMfrat business intelligence',
                    },
                    {
                      name: 'Content Planner',
                      icon: <ApprovalIcon />,
                      tooltip: 'Build courses in Content Pipeline',
                    },
                    {
                      name: 'Content Analyzer',
                      icon: <BarChartIcon />,
                      tooltip: 'Manage accreditations in Content Pipeline',
                    },
                    {
                      name: 'Content Liaison',
                      icon: <PolicyIcon />,
                      tooltip: 'Manage accreditations in Content Pipeline',
                    },
                    {
                      name: 'Content Instructor',
                      icon: <PeopleAltIcon />,
                      tooltip:
                        'View live class schedule and sign up for events',
                    },
                    {
                      name: 'Codex Editor',
                      icon: <MenuBookIcon />,
                      tooltip:
                        'Create and manage FOAMfrat intellectual property in Codex',
                    },
                  ].map((role, i) => {
                    let roleName = role.name
                      .toLocaleLowerCase()
                      .replace(/\s+/g, '-')
                    let hasRole = userRoles.some(
                      (ur) => ur.roleName === roleName,
                    )
                    let isStatic = role.name === 'Student' && hasRole

                    return (
                      <Tooltip key={i} title={role.tooltip} arrow>
                        <Chip
                          label={role.name}
                          color="primary"
                          variant={hasRole ? 'filled' : 'outlined'}
                          icon={role.icon}
                          clickable={!isStatic}
                          onClick={() => {
                            if (!isStatic) {
                              setRoleToChange(roleName)
                              setModalStatus('EditUserRole')
                            }
                          }}
                        />
                      </Tooltip>
                    )
                  })}
                </Box>
                <Divider sx={{ marginY: 1 }} />
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    License
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={onLicenseEdit}
                    sx={{
                      '&:hover': { bgcolor: 'background.paperDark' },
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Typography variant="body2">
                    <strong>Number:</strong> {user?.licenseNumber || 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Type:</strong> {user?.licenseType || 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>State:</strong> {user?.licenseState || 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Expiration:</strong>{' '}
                    {user?.licenseExpiration
                      ? dayjs(user?.licenseExpiration).format('MM/DD/YYYY')
                      : 'Unknown'}
                  </Typography>
                </Box>
                <Divider sx={{ marginY: 1 }} />
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    NREMT Certification
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={onNremtEdit}
                    sx={{
                      '&:hover': { bgcolor: 'background.paperDark' },
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Typography variant="body2">
                    <strong>Number:</strong>{' '}
                    {user?.nremtCertificationNumber || 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Expiration Date:</strong>{' '}
                    {user?.nremtCertificationDate
                      ? dayjs(user?.nremtCertificationDate).format('MM/DD/YYYY')
                      : 'Unknown'}
                  </Typography>
                  {user?.nremtIssueDate && (
                    <Typography variant="body2">
                      <strong>Issue Date:</strong>{' '}
                      {user?.nremtIssueDate
                        ? dayjs(user?.nremtIssueDate).format('MM/DD/YYYY')
                        : 'Unknown'}
                    </Typography>
                  )}
                </Box>
                <Divider sx={{ marginY: 1 }} />
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Subscription Information
                  </Typography>
                  <IconButton
                    size="small"
                    disabled={!userSubscription}
                    onClick={() => {
                      setSubscriptionID(userSubscription?.subscriptionID || '')
                      setSubscriptionStatus(
                        userSubscription?.subscriptionStatus || '',
                      )
                      setFormOpen(true)
                    }}
                    sx={{
                      '&:hover': { bgcolor: 'background.paperDark' },
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Typography variant="body2">
                    <strong>ID:</strong>{' '}
                    {userSubscription?.subscriptionID || 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Status:</strong>{' '}
                    {userSubscription?.subscriptionStatus
                      ? `${userSubscription.subscriptionStatus[0].toUpperCase()}${userSubscription.subscriptionStatus.slice(1)}`
                      : 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Expiration:</strong>{' '}
                    {userSubscription?.subscriptionEndDate
                      ? dayjs(userSubscription?.subscriptionEndDate).format(
                          'MM/DD/YYYY',
                        )
                      : 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Customer ID:</strong>{' '}
                    {userSubscription?.customerID || 'Unknown'}
                  </Typography>
                </Box>
                <Dialog open={formOpen} onClose={closeSubscriptionForm}>
                  <Paper
                    sx={{
                      width: 300,
                      px: 2,
                      py: 1,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <DialogTitle
                      sx={{ textAlign: 'center', marginBottom: -1.5 }}
                    >
                      Update Subscription Info
                    </DialogTitle>
                    <DialogContent
                      sx={{
                        px: 2,
                        pb: 2,
                        display: 'flex',
                        gap: 2,
                        flexDirection: 'column',
                      }}
                    >
                      <TextField
                        sx={{
                          mt: 1,
                          '& .MuiOutlinedInput-root': {
                            bgcolor: 'background.paperDark',
                          },
                        }}
                        variant="outlined"
                        size="small"
                        label="Subscription ID"
                        type="text"
                        fullWidth
                        value={subscriptionID}
                        onChange={(e: React.ChangeEvent) =>
                          setSubscriptionID(
                            (e.target as HTMLInputElement).value,
                          )
                        }
                      />
                      <FormControl fullWidth size="small">
                        <InputLabel>Subscription Status</InputLabel>
                        <Select
                          fullWidth
                          label="Subscription Status"
                          value={subscriptionStatus}
                          onChange={(e) =>
                            setSubscriptionStatus(e.target.value)
                          }
                        >
                          {userSubscription?.subscriptionStatus ===
                            'pending' && (
                            <MenuItem key="pending" value="pending">
                              Pending
                            </MenuItem>
                          )}
                          {userSubscription?.subscriptionStatus === 'trial' && (
                            <MenuItem key="trial" value="trial">
                              Trial
                            </MenuItem>
                          )}
                          {['Active', 'Cancelled'].map((s) => (
                            <MenuItem key={s} value={s.toLowerCase()}>
                              {s}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </DialogContent>
                    <DialogActions
                      sx={{ display: 'flex', justifyContent: 'center', pb: 3 }}
                    >
                      {formSaving ? (
                        <CircularProgress size={36} />
                      ) : (
                        <>
                          <Button
                            onClick={onUserSubscriptionUpdated}
                            color="primary"
                            variant="contained"
                            sx={{ flex: '1 1 0px' }}
                            startIcon={<SubmitIcon />}
                            disabled={
                              !subscriptionID ||
                              !(
                                subscriptionStatus === 'active' ||
                                subscriptionStatus === 'cancelled'
                              )
                            }
                          >
                            Submit
                          </Button>
                          <Button
                            onClick={closeSubscriptionForm}
                            color="error"
                            variant="contained"
                            sx={{ flex: '1 1 0px' }}
                            startIcon={<CancelIcon />}
                          >
                            Cancel
                          </Button>
                        </>
                      )}
                    </DialogActions>
                  </Paper>
                </Dialog>
              </Box>
            </Paper>
            <Box sx={{ mt: 4 }}>
              <Subheader title="Linked Services" />
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Box sx={{ gridColumn: '1' }}>
                  <Card>
                    <CardContent>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, mb: 2 }}
                        align="center"
                        gutterBottom
                      >
                        Add To Linked Service
                      </Typography>
                      <Autocomplete
                        className="mx-auto my-4"
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        getOptionLabel={(option) => `${option.name}`}
                        onInputChange={(_, value) =>
                          setSelectedLinkedService(value)
                        }
                        disablePortal
                        options={allLinkedServices}
                        sx={{ width: 300 }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Linked Service"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                bgcolor: 'background.paperDark',
                              },
                            }}
                          />
                        )}
                      />
                    </CardContent>
                    {!!linkedServiceToAdd && (
                      <>
                        <Box
                          sx={{
                            width: '100%',
                            aspectRatio: '16/9',
                            backgroundImage: `url(${linkedServiceToAdd.imageName})`,
                            backgroundSize: 'cover',
                            border: 0,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center',
                            borderRadius: 1,
                            overflow: 'hidden',
                          }}
                        />
                        <CardContent>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 500 }}
                          >
                            {linkedServiceToAdd?.name}
                          </Typography>
                        </CardContent>
                      </>
                    )}
                    <CardActions sx={{ pt: 0 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        disabled={!linkedServiceToAdd?.name}
                        color="primary"
                        onClick={
                          compose(
                            setModalStatus,
                            always('AddLinkedService') as any,
                          ) as any
                        }
                        sx={{ mx: 2, mb: 1 }}
                      >
                        Add Service
                      </Button>
                    </CardActions>
                  </Card>
                </Box>
                <Box
                  sx={{
                    gridColumn: 'span 2',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    alignItems: 'center',
                  }}
                >
                  {linkedServices.map((service) => (
                    <Chip
                      key={service.id}
                      avatar={
                        <Avatar src={service.imageName}>
                          {service.name.toUpperCase()}
                        </Avatar>
                      }
                      label={service.name}
                      onDelete={pipe(
                        compose(
                          setLinkedServiceToRemove,
                          always(service.id) as any,
                        ) as any,
                        compose(
                          setModalStatus,
                          always('DeleteLinkedService') as any,
                        ) as any,
                      )}
                      color="primary"
                      deleteIcon={<CloseIcon />}
                      sx={{ mb: 1 }}
                    />
                  ))}
                </Box>
              </div>
            </Box>

            {/* Interventions Section */}
            <Box sx={{ mt: 4 }}>
              <Subheader title="Interventions" />
              <Interventions userID={user?.id || ''} />
            </Box>

            <Box sx={{ mt: 4 }}>
              <Subheader title="Danger Zone" />
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  bgcolor: 'background.default',
                  border: '1px solid',
                  borderColor: 'error.main',
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Permanently delete this user account. This action cannot be
                  undone.
                </Typography>
                <Button
                  color="error"
                  variant="contained"
                  onClick={
                    compose(setModalStatus, always('DeleteUser') as any) as any
                  }
                >
                  Delete User
                </Button>
              </Paper>
            </Box>
          </>
        )}
      </Container>
      <ConfirmAddToLinkedService />
      <ConfirmRemoveFromLinkedService />
      <ConfirmChangeUserRole />
      <ConfirmDeleteUser />
      <ReallyConfirmDeleteUser />
      <ConfirmBoundTreeLimited />
      <ConfirmBoundTreeFull />
      <ConfirmRemoveBoundtree />
      <Dialog
        open={userFormStatus === 'user'}
        onClose={() => setUserFormStatus('')}
      >
        <form onSubmit={userForm.handleSubmit}>
          <Paper sx={{ width: 300, px: 2, py: 1, bgcolor: 'background.paper' }}>
            <DialogTitle sx={{ textAlign: 'center', marginBottom: -1.5 }}>
              Edit User Information
            </DialogTitle>
            <DialogContent
              sx={{
                px: 2,
                pb: 1,
                display: 'flex',
                gap: 2,
                flexDirection: 'column',
              }}
            >
              <TextField
                fullWidth
                variant="standard"
                value={userForm.values.firstName}
                label="First Name"
                name="firstName"
                onChange={userForm.handleChange}
                error={
                  userForm.touched.firstName && !!userForm.errors.firstName
                }
                sx={{ '& .MuiInput-root': { bgcolor: 'background.paperDark' } }}
              />
              <TextField
                fullWidth
                variant="standard"
                value={userForm.values.lastName}
                label="Last Name"
                name="lastName"
                onChange={userForm.handleChange}
                error={userForm.touched.lastName && !!userForm.errors.lastName}
              />
              <TextField
                fullWidth
                variant="standard"
                value={userForm.values.phone}
                label="Phone"
                name="phone"
                onChange={userForm.handleChange}
                error={userForm.touched.phone && !!userForm.errors.phone}
              />
              <TextField
                fullWidth
                variant="standard"
                value={userForm.values.username}
                label="Username"
                name="username"
                onChange={userForm.handleChange}
                error={userForm.touched.username && !!userForm.errors.username}
              />
              <TextField
                fullWidth
                variant="standard"
                value={userForm.values.email}
                label="Email"
                name="email"
                onChange={userForm.handleChange}
                error={userForm.touched.email && !!userForm.errors.email}
              />
              <TextField
                fullWidth
                variant="standard"
                value={userForm.values.address}
                label="Address"
                name="address"
                onChange={userForm.handleChange}
                error={userForm.touched.address && !!userForm.errors.address}
              />
              <TextField
                fullWidth
                variant="standard"
                value={userForm.values.city}
                label="City"
                name="city"
                onChange={userForm.handleChange}
                error={userForm.touched.city && !!userForm.errors.city}
              />
              <StateDropdown
                value={userForm.values.state}
                onChange={userForm.handleChange}
                error={userForm.touched.state && !!userForm.errors.state}
                bgDefault
              />
              <TextField
                fullWidth
                variant="standard"
                value={userForm.values.zip}
                label="ZIP"
                name="zip"
                onChange={userForm.handleChange}
                error={userForm.touched.zip && !!userForm.errors.zip}
              />
            </DialogContent>
            <DialogActions
              sx={{
                display: 'flex',
                justifyContent: 'center',
                paddingBottom: 3,
              }}
            >
              {saving ? (
                <CircularProgress size={36} />
              ) : (
                <>
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    sx={{ flex: '1 1 0px' }}
                    startIcon={<SubmitIcon />}
                  >
                    Submit
                  </Button>
                  <Button
                    onClick={() => setUserFormStatus('')}
                    color="error"
                    variant="contained"
                    sx={{ flex: '1 1 0px' }}
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </DialogActions>
          </Paper>
        </form>
      </Dialog>
      <Dialog
        open={userFormStatus === 'license'}
        onClose={() => setUserFormStatus('')}
      >
        <form onSubmit={licenseForm.handleSubmit}>
          <Paper sx={{ width: 300, px: 2, py: 1, bgcolor: 'background.paper' }}>
            <DialogTitle sx={{ textAlign: 'center', marginBottom: -1.5 }}>
              Edit License Information
            </DialogTitle>
            <DialogContent
              sx={{
                px: 2,
                pb: 1,
                display: 'flex',
                gap: 2,
                flexDirection: 'column',
              }}
            >
              <TextField
                fullWidth
                variant="standard"
                value={licenseForm.values.licenseNumber}
                label="License Number"
                name="licenseNumber"
                onChange={licenseForm.handleChange}
                error={
                  licenseForm.touched.licenseNumber &&
                  !!licenseForm.errors.licenseNumber
                }
              />
              <FormControl fullWidth>
                <InputLabel sx={{ marginLeft: -2 }}>License Type</InputLabel>
                <Select
                  variant="standard"
                  value={licenseForm.values.licenseType}
                  label="License Type"
                  name="licenseType"
                  onChange={licenseForm.handleChange}
                  error={
                    licenseForm.touched.licenseType &&
                    !!licenseForm.errors.licenseType
                  }
                >
                  <MenuItem key="unselected" value="unselected">
                    Unselected
                  </MenuItem>
                  <MenuItem value="" />
                  <MenuItem value="Paramedic">Paramedic</MenuItem>
                  <MenuItem value="Registered Nurse">Registered Nurse</MenuItem>
                  <MenuItem value="EMR">Emergency Medical Responder</MenuItem>
                  <MenuItem value="CFR">Certified First Responder</MenuItem>
                  <MenuItem value="EMT">EMT</MenuItem>
                  <MenuItem value="EMT-1">EMT-1</MenuItem>
                  <MenuItem value="EMT-B">EMT-B</MenuItem>
                  <MenuItem value="EMT-D">EMT-D</MenuItem>
                  <MenuItem value="AEMT">AEMT</MenuItem>
                  <MenuItem value="EMT-2">EMT-2</MenuItem>
                  <MenuItem value="EMT-CC">EMT-CC</MenuItem>
                  <MenuItem value="Advanced Care Paramedic (Canada Only)">
                    Advanced Care Paramedic (Canada Only)
                  </MenuItem>
                  <MenuItem value="Primary Care Paramedic (Canada Only)">
                    Primary Care Paramedic (Canada Only)
                  </MenuItem>
                  <MenuItem value="Physician">Physician</MenuItem>
                  <MenuItem value="Physician Assistant">
                    Physician Assistant
                  </MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
              <StateDropdown
                value={licenseForm.values.licenseState}
                label="License State"
                name="licenseState"
                onChange={licenseForm.handleChange}
                error={
                  licenseForm.touched.licenseState &&
                  !!licenseForm.errors.licenseState
                }
                bgDefault
              />
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="License Expiration Date"
                  value={dayjs(licenseForm.values.licenseExpiration)}
                  onChange={(dayJsVal: any) =>
                    licenseForm.handleChange({
                      target: {
                        name: 'licenseExpiration',
                        value: dayjs(dayJsVal).format('MM/DD/YYYY'),
                      },
                    })
                  }
                />
              </LocalizationProvider>
            </DialogContent>
            <DialogActions
              sx={{
                display: 'flex',
                justifyContent: 'center',
                paddingBottom: 3,
              }}
            >
              {saving ? (
                <CircularProgress size={36} />
              ) : (
                <>
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    sx={{ flex: '1 1 0px' }}
                    startIcon={<SubmitIcon />}
                  >
                    Submit
                  </Button>
                  <Button
                    onClick={() => setUserFormStatus('')}
                    color="error"
                    variant="contained"
                    sx={{ flex: '1 1 0px' }}
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </DialogActions>
          </Paper>
        </form>
      </Dialog>
      <Dialog
        open={userFormStatus === 'nremt'}
        onClose={() => setUserFormStatus('')}
      >
        <form onSubmit={nremtForm.handleSubmit}>
          <Paper sx={{ width: 300, px: 2, py: 1, bgcolor: 'background.paper' }}>
            <DialogTitle sx={{ textAlign: 'center', marginBottom: -1.5 }}>
              Edit NREMT Information
            </DialogTitle>
            <DialogContent
              sx={{
                px: 2,
                pb: 1,
                display: 'flex',
                gap: 2,
                flexDirection: 'column',
              }}
            >
              <TextField
                fullWidth
                variant="standard"
                value={nremtForm.values.nremtCertificationNumber}
                label="NREMT Certification Number"
                name="nremtCertificationNumber"
                onChange={nremtForm.handleChange}
                error={
                  nremtForm.touched.nremtCertificationNumber &&
                  !!nremtForm.errors.nremtCertificationNumber
                }
              />
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="NREMT Expiration Date"
                  value={dayjs(nremtForm?.values?.nremtCertificationDate)}
                  onChange={(dayJsVal: any) =>
                    nremtForm.handleChange({
                      target: {
                        name: 'nremtCertificationDate',
                        value: dayjs(dayJsVal).format('MM/DD/YYYY'),
                      },
                    })
                  }
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="NREMT Issue Date"
                  value={dayjs(nremtForm?.values?.issueDate)}
                  onChange={(dayJsVal: any) =>
                    nremtForm.handleChange({
                      target: {
                        name: 'issueDate',
                        value: dayJsVal
                          ? dayjs(dayJsVal).format('MM/DD/YYYY')
                          : '',
                      },
                    })
                  }
                />
              </LocalizationProvider>
            </DialogContent>
            <DialogActions
              sx={{
                display: 'flex',
                justifyContent: 'center',
                paddingBottom: 3,
              }}
            >
              {saving ? (
                <CircularProgress size={36} />
              ) : (
                <>
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    sx={{ flex: '1 1 0px' }}
                    startIcon={<SubmitIcon />}
                  >
                    Submit
                  </Button>
                  <Button
                    onClick={() => setUserFormStatus('')}
                    color="error"
                    variant="contained"
                    sx={{ flex: '1 1 0px' }}
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </DialogActions>
          </Paper>
        </form>
      </Dialog>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          <strong>Error with request: </strong>
          {errorMessage}
        </Alert>
      </Snackbar>
      <Snackbar open={!!successMessage} onClose={() => setSuccessMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setSuccessMessage('')}
          severity="success"
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  )
}
